import React from "react";
import PageWrapper from "components/PageWrapper";
import SampleDataNotice from "components/SampleDataNotice";
import {
  DollarSign,
  ShoppingCart,
  Clock,
  Truck,
  ArrowUp,
  ArrowDown,
  Download,
  Calendar,
  ChevronDown,
} from "lucide-react";

const SUPPLIER_SHARE = [
  { name: "Digi-Key", short: "DK", pct: 42, spend: 102900, key: "digikey", bar: "bg-s360-violet", wm: "bg-[#CC0000]" },
  { name: "Mouser", short: "Mou", pct: 31, spend: 75950, key: "mouser", bar: "bg-s360-teal", wm: "bg-[#005DAA]" },
  { name: "Element 14", short: "E14", pct: 15, spend: 36750, key: "element14", bar: "bg-s360-warning", wm: "bg-[#FF6600]" },
  { name: "Arrow", short: "Arr", pct: 12, spend: 29400, key: "arrow", bar: "bg-s360-violet", wm: "bg-[#0B7D72]" },
];

const TOP_PARTS = [
  { pn: "STM32F407VGT6", spend: 24180, units: 2870, change: "+18%" },
  { pn: "LM2596S-ADJ/NOPB", spend: 18920, units: 8400, change: "+9%" },
  { pn: "NRF52840-QIAA-R", spend: 14250, units: 1900, change: "-4%" },
  { pn: "MAX9939AUB+", spend: 9450, units: 760, change: "+22%" },
  { pn: "TPS54622RHLR", spend: 7160, units: 3870, change: "+3%" },
];

const SAVINGS_DATA = [3.2, 4.1, 3.8, 5.0, 4.6, 5.8, 6.2, 5.9, 7.3, 6.8, 8.2, 9.1];
const MONTHS = ["Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec", "Jan", "Feb", "Mar", "Apr", "May"];

function buildPath(data, width, height, padding = 8) {
  const max = Math.max(...data);
  const min = Math.min(...data);
  const range = max - min || 1;
  const stepX = (width - padding * 2) / (data.length - 1);
  const points = data.map((v, i) => [
    padding + i * stepX,
    height - padding - ((v - min) / range) * (height - padding * 2),
  ]);
  const linePath = "M " + points.map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join(" L ");
  const areaPath =
    linePath +
    ` L ${(width - padding).toFixed(1)},${(height - padding).toFixed(1)} L ${padding.toFixed(1)},${(height - padding).toFixed(1)} Z`;
  return { linePath, areaPath, points };
}

const StatCard = ({ tone, Icon, label, value, unit, delta, deltaDir = "up", note }) => {
  const t = {
    violet: { accent: "from-s360-violet to-s360-violet-300", bubble: "bg-s360-violet-50 text-s360-violet" },
    amber: { accent: "from-s360-warning to-[#FFB000]", bubble: "bg-s360-warning-bg text-s360-warning" },
    teal: { accent: "from-s360-teal to-[#5EE7DA]", bubble: "bg-s360-teal-50 text-s360-teal" },
    green: { accent: "from-s360-success to-[#4CAF50]", bubble: "bg-s360-success-bg text-s360-success" },
  }[tone];
  return (
    <div className="relative overflow-hidden bg-white rounded-md shadow-sm p-[18px] flex flex-col gap-3">
      <div className={`absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r ${t.accent}`} />
      <div className="flex items-start justify-between gap-3">
        <div className="flex flex-col gap-1">
          <span className="text-xs font-semibold uppercase tracking-[0.06em] text-s360-fg3">
            {label}
          </span>
          <span className="text-[32px] leading-[38px] font-bold tracking-[-0.02em] text-s360-ink tabular-nums">
            {value}
            {unit && <span className="text-base text-s360-fg3 ml-1">{unit}</span>}
          </span>
        </div>
        <span className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${t.bubble}`}>
          <Icon className="w-5 h-5" />
        </span>
      </div>
      <div className="flex items-center gap-1.5 text-xs text-s360-fg3">
        <span
          className={`inline-flex items-center gap-0.5 font-semibold tabular-nums ${
            deltaDir === "up" ? "text-s360-success" : "text-s360-danger"
          }`}
        >
          {deltaDir === "up" ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}
          {delta}
        </span>
        <span>{note}</span>
      </div>
    </div>
  );
};

const AnalyticsPage = () => {
  const totalSpend = SUPPLIER_SHARE.reduce((s, d) => s + d.spend, 0);
  const ytdSavings = SAVINGS_DATA.reduce((s, v) => s + v, 0);
  const lastMonth = SAVINGS_DATA[SAVINGS_DATA.length - 1];
  const prevMonth = SAVINGS_DATA[SAVINGS_DATA.length - 2];
  const monthDelta = (((lastMonth - prevMonth) / prevMonth) * 100).toFixed(1);
  const topPartsTotal = TOP_PARTS.reduce((s, x) => s + x.spend, 0);
  const { linePath, areaPath, points } = buildPath(SAVINGS_DATA, 640, 120);

  return (
    <PageWrapper>
      <div className="flex flex-col gap-4">
        <SampleDataNotice what="figures and charts" />
        {/* Page head */}
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div className="flex flex-col gap-1.5">
            <h1 className="text-[28px] leading-[34px] font-bold tracking-[-0.015em] text-s360-ink">
              Analytics
            </h1>
            <span className="text-sm text-s360-fg3">
              Spend, savings, supplier mix, and lead-time trends for your procurement activity.
            </span>
          </div>
          <div className="flex gap-2">
            <button className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md bg-white border border-s360-stroke1 text-[13px] font-semibold text-s360-fg2 hover:bg-s360-bg2">
              <Calendar className="w-3.5 h-3.5" /> Last 12 months <ChevronDown className="w-3 h-3" />
            </button>
            <button className="inline-flex items-center gap-1.5 h-8 px-3.5 rounded-md bg-s360-violet text-white text-[13px] font-semibold hover:bg-s360-violet-600">
              <Download className="w-3.5 h-3.5" /> Export
            </button>
          </div>
        </div>

        {/* Stat row */}
        <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            tone="violet"
            Icon={DollarSign}
            label="Total spend (YTD)"
            value={`$${(totalSpend / 1000).toFixed(0)}K`}
            delta="+18%"
            note="vs last year"
          />
          <StatCard tone="amber" Icon={ShoppingCart} label="Orders processed" value="127" delta="+23%" note="from last month" />
          <StatCard
            tone="teal"
            Icon={Clock}
            label="Avg lead time"
            value="2.3"
            unit="days"
            delta="-3%"
            deltaDir="down"
            note="improving"
          />
          <StatCard tone="green" Icon={Truck} label="On-time delivery" value="96%" delta="+2%" note="vs prior 30d" />
        </section>

        {/* Supplier share + savings trend */}
        <section className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
          {/* Supplier share */}
          <div className="bg-white rounded-md shadow-sm flex flex-col">
            <div className="px-[18px] py-3.5 border-b border-s360-stroke1 flex items-start justify-between gap-3">
              <div>
                <h3 className="text-base font-semibold text-s360-fg1 m-0">Supplier share of spend</h3>
                <div className="text-xs text-s360-fg3 mt-0.5">
                  Distribution across {SUPPLIER_SHARE.length} connected suppliers
                </div>
              </div>
              <span className="font-mono text-lg font-bold text-s360-ink">
                ${(totalSpend / 1000).toFixed(0)}K
              </span>
            </div>
            <div className="p-[18px] flex flex-col gap-4">
              {SUPPLIER_SHARE.map((s) => (
                <div key={s.key} className="flex flex-col gap-1.5">
                  <div className="flex items-center justify-between">
                    <span className="inline-flex items-center gap-2 text-[13px] text-s360-fg1">
                      <span
                        className={`w-[22px] h-[22px] rounded-[4px] inline-flex items-center justify-center text-[9px] font-bold text-white ${s.wm}`}
                      >
                        {s.short}
                      </span>
                      {s.name}
                    </span>
                    <span className="font-mono text-[13px] font-semibold text-s360-fg1">
                      ${(s.spend / 1000).toFixed(1)}K{" "}
                      <span className="text-s360-fg3 font-medium text-xs">· {s.pct}%</span>
                    </span>
                  </div>
                  <div className="h-2 rounded-full bg-s360-bg2 overflow-hidden">
                    <span className={`block h-full rounded-full ${s.bar}`} style={{ width: `${s.pct}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Savings trend sparkline */}
          <div className="bg-white rounded-md shadow-sm p-[18px] flex flex-col gap-1">
            <h3 className="text-base font-semibold text-s360-fg1 m-0">Cost savings trend</h3>
            <span className="text-xs text-s360-fg3">Monthly savings vs list price · last 12 months</span>
            <div className="flex items-baseline gap-3 mt-2 mb-1">
              <span className="font-mono text-[28px] leading-9 font-bold text-s360-success tabular-nums">
                ${ytdSavings.toFixed(1)}K
              </span>
              <span
                className={`inline-flex items-center gap-0.5 text-xs font-semibold ${
                  Number(monthDelta) < 0 ? "text-s360-danger" : "text-s360-success"
                }`}
              >
                {Number(monthDelta) >= 0 ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}
                {monthDelta}% vs prior month
              </span>
            </div>
            <svg viewBox="0 0 640 120" preserveAspectRatio="none" className="w-full h-[120px]">
              <defs>
                <linearGradient id="sparkGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#5F47C9" stopOpacity="0.5" />
                  <stop offset="100%" stopColor="#5F47C9" stopOpacity="0" />
                </linearGradient>
              </defs>
              <path d={areaPath} fill="url(#sparkGrad)" />
              <path d={linePath} fill="none" stroke="#5F47C9" strokeWidth="2" vectorEffect="non-scaling-stroke" />
              {points.map(([x, y], i) => (
                <circle key={i} cx={x} cy={y} r="3" fill="#5F47C9" />
              ))}
            </svg>
            <div className="grid mt-[-8px]" style={{ gridTemplateColumns: `repeat(${MONTHS.length}, 1fr)` }}>
              {MONTHS.map((m, i) => (
                <span key={i} className="text-[10px] text-s360-fg3 text-center">
                  {m}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* Top spending parts */}
        <div className="bg-white rounded-md shadow-sm flex flex-col overflow-hidden">
          <div className="px-[18px] py-3.5 border-b border-s360-stroke1 flex items-center gap-3 flex-wrap">
            <div>
              <h3 className="text-base font-semibold text-s360-fg1 m-0">Top spending parts</h3>
              <span className="text-xs text-s360-fg3">Highest-spend MPNs in the last 12 months</span>
            </div>
            <button className="ml-auto inline-flex items-center gap-1.5 h-[30px] px-3 rounded-md bg-white border border-s360-stroke1 text-xs font-semibold text-s360-fg1 hover:bg-s360-bg2">
              <Download className="w-3.5 h-3.5" /> Export
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr>
                  {[
                    { label: "#", cls: "w-14 text-center" },
                    { label: "Part number", cls: "text-left" },
                    { label: "Units", cls: "text-right" },
                    { label: "Spend", cls: "text-right" },
                    { label: "Trend", cls: "text-right" },
                    { label: "Share of total", cls: "text-right w-[220px]" },
                  ].map((c) => (
                    <th
                      key={c.label}
                      className={`py-2.5 px-3.5 text-[11px] font-semibold uppercase tracking-[0.08em] text-s360-fg3 bg-s360-bg2 border-b border-s360-stroke1 whitespace-nowrap ${c.cls}`}
                    >
                      {c.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {TOP_PARTS.map((p, idx) => {
                  const pct = (p.spend / topPartsTotal) * 100;
                  const isPos = p.change.startsWith("+");
                  return (
                    <tr key={p.pn} className="border-b border-s360-bg2 last:border-0 hover:bg-s360-stripe">
                      <td className="py-3 px-3.5 text-center font-mono text-xs text-s360-fg3">{idx + 1}</td>
                      <td className="py-3 px-3.5">
                        <span className="font-mono text-[13px] font-semibold text-s360-violet">{p.pn}</span>
                      </td>
                      <td className="py-3 px-3.5 text-right font-mono text-[13px] text-s360-fg2 tabular-nums">
                        {p.units.toLocaleString()}
                      </td>
                      <td className="py-3 px-3.5 text-right font-mono text-[13px] font-bold text-s360-ink tabular-nums">
                        ${p.spend.toLocaleString()}
                      </td>
                      <td className="py-3 px-3.5 text-right">
                        <span
                          className={`inline-flex items-center gap-0.5 font-mono text-xs font-semibold ${
                            isPos ? "text-s360-success" : "text-s360-danger"
                          }`}
                        >
                          {isPos ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}
                          {p.change}
                        </span>
                      </td>
                      <td className="py-3 px-3.5">
                        <div className="flex items-center gap-2.5 justify-end">
                          <span className="h-1.5 w-[140px] rounded-full bg-s360-bg2 overflow-hidden">
                            <span className="block h-full bg-s360-violet" style={{ width: `${pct}%` }} />
                          </span>
                          <span className="font-mono text-xs font-semibold text-s360-fg2 min-w-[36px] text-right">
                            {pct.toFixed(1)}%
                          </span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </PageWrapper>
  );
};

export default AnalyticsPage;
